import { Chip, SxProps, Theme } from "@mui/material";
import { CHART_COLORS, CHART_TYPOGRAPHY } from "./app/provider/patient/chart";

type StatusVariant =
  "success" | "error" | "warning" | "info" | "default" | "neutral" | "pending";

const VARIANT_STYLES: Record<
  StatusVariant,
  { color: string; bgColor: string }
> = {
  success: {
    color: CHART_COLORS.badgeSuccessText,
    bgColor: CHART_COLORS.badgeSuccessBg,
  },
  error: {
    color: CHART_COLORS.badgeErrorText,
    bgColor: CHART_COLORS.badgeErrorBg,
  },
  warning: {
    color: CHART_COLORS.badgeWarningText,
    bgColor: CHART_COLORS.badgeWarningBg,
  },
  info: {
    color: CHART_COLORS.badgeInfoText,
    bgColor: CHART_COLORS.badgeInfoBg,
  },
  default: {
    color: CHART_COLORS.badgeDefaultText,
    bgColor: CHART_COLORS.badgeDefaultBg,
  },
  neutral: {
    color: CHART_COLORS.badgeNeutralText,
    bgColor: CHART_COLORS.badgeNeutralBg,
  },
  pending: {
    color: CHART_COLORS.badgePendingText,
    bgColor: CHART_COLORS.badgePendingBg,
  },
};

type StatusChipProps = {
  /** Boolean for active/inactive, string for custom label display */
  value?: boolean | string | null | undefined;
  /** Direct label override — takes highest priority */
  label?: string;
  /** Predefined color variant */
  variant?: StatusVariant;
  /** Custom text color — overrides variant */
  color?: string;
  /** Custom background color — overrides variant */
  bgColor?: string;
  /** Label when value is true (default: "Active") */
  activeLabel?: string;
  /** Label when value is false (default: "Inactive") */
  inactiveLabel?: string;
  /** Additional sx overrides */
  sx?: SxProps<Theme>;
};

const StatusChip = ({
  value,
  label,
  variant,
  color,
  bgColor,
  activeLabel = "Active",
  inactiveLabel = "Inactive",
  sx,
}: StatusChipProps) => {
  // final label
  const finalLabel =
    label ??
    (typeof value === "string" ? value : undefined) ??
    (typeof value === "boolean" || value == null
      ? !!value
        ? activeLabel
        : inactiveLabel
      : "-");

  // final colors: custom > variant > boolean fallback
  const finalColors = () => {
    if (color && bgColor) return { color, bgColor };
    if (variant)
      return {
        ...VARIANT_STYLES[variant],
        ...(color ? { color } : {}),
        ...(bgColor ? { bgColor } : {}),
      };

    // Boolean fallback
    if (typeof value === "boolean" || (!label && !variant && value == null)) {
      return !!value ? VARIANT_STYLES.success : VARIANT_STYLES.error;
    }

    // String/label with no variant — default to info
    return VARIANT_STYLES.info;
  };

  const colors = finalColors();

  return (
    <Chip
      size="small"
      label={finalLabel}
      sx={{
        color: colors.color,
        backgroundColor: colors.bgColor,
        fontWeight: CHART_TYPOGRAPHY.weight.regular,
        height: "18px",
        fontSize: CHART_TYPOGRAPHY.body5.fontSize,
        ...(sx as object),
      }}
    />
  );
};

export default StatusChip;
